import { ArrowRight, Check, ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Money } from '@/components/ui/Money'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { addDecimals, toNumber } from '@/lib/decimal'
import { formatEuros, formatHours } from '@/lib/format'
import { useGenerateFromHours, usePreviewFromHours } from '@/lib/invoice-queries'
import type { MonthlyPreviewClient } from '@/lib/invoice-types'
import { useApproveWeekAction } from '@/lib/time-queries'
import type { WeekApproval } from '@/lib/types'
import { cn } from '@/lib/utils'
import { weekMetaFromId } from '@/lib/week'

function defaultMonth(): string {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`
}

function monthRange(month: string): { periodStart: string; periodEnd: string } {
  const [y, m] = month.split('-').map(Number)
  const end = new Date(y, m, 0).getDate()
  return { periodStart: `${month}-01`, periodEnd: `${month}-${String(end).padStart(2, '0')}` }
}

export function MonthlyWizard({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [step, setStep] = useState<'period' | 'preview'>('period')
  const [month, setMonth] = useState(defaultMonth)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [deselectedProjects, setDeselectedProjects] = useState<Set<string>>(new Set())

  const preview = usePreviewFromHours()
  const generate = useGenerateFromHours()

  function reset() {
    setStep('period')
    setSelected(new Set())
    setDeselectedProjects(new Set())
    preview.reset()
    generate.reset()
  }

  function close(next: boolean) {
    onOpenChange(next)
    if (!next) reset()
  }

  function runPreview() {
    preview.mutate(monthRange(month), {
      onSuccess: (data) => {
        setSelected(new Set(data.clients.map((c) => c.clientId)))
        setStep('preview')
      },
    })
  }

  function toggle(clientId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(clientId)) next.delete(clientId)
      else next.add(clientId)
      return next
    })
  }

  function toggleProject(projectId: string) {
    setDeselectedProjects((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }

  const clients = preview.data?.clients ?? []

  function runGenerate() {
    const { periodStart, periodEnd } = monthRange(month)
    const selectedClients = clients.filter((c) => selected.has(c.clientId))
    const clientProjectIds = selectedClients.flatMap((c) => c.projectBreakdown.map((p) => p.projectId))
    const filtering = clientProjectIds.some((id) => deselectedProjects.has(id))
    const projectIds = filtering ? clientProjectIds.filter((id) => !deselectedProjects.has(id)) : undefined
    generate.mutate(
      { periodStart, periodEnd, clientIds: [...selected], projectIds },
      { onSuccess: () => close(false) },
    )
  }

  function refreshPreview() {
    preview.mutate(monthRange(month))
  }

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="Maandfacturen genereren"
      description={step === 'period' ? 'Kies de periode om ongefactureerde uren te bundelen.' : 'Selecteer de klanten waarvoor je een concept wilt aanmaken.'}
      footer={
        step === 'period' ? (
          <>
            <button type="button" className="btn-ghost" onClick={() => close(false)}>
              Annuleren
            </button>
            <button type="button" className="btn-primary" onClick={runPreview} disabled={preview.isPending}>
              {preview.isPending ? 'Laden…' : 'Toon preview'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-ghost" onClick={() => setStep('period')}>
              Terug
            </button>
            <button type="button" className="btn-primary" onClick={runGenerate} disabled={selected.size === 0 || generate.isPending}>
              {generate.isPending ? 'Aanmaken…' : `Maak ${selected.size} concept(en)`}
            </button>
          </>
        )
      }
    >
      {step === 'period' ? (
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Maand</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="field" />
        </label>
      ) : preview.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ) : clients.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-faint">Geen ongefactureerde uren in deze periode.</p>
      ) : (
        <div className="space-y-2">
          <ApprovalBlock clients={clients} onApproved={refreshPreview} />
          {clients.map((client) => (
            <PreviewRow
              key={client.clientId}
              client={client}
              selected={selected.has(client.clientId)}
              onToggle={() => toggle(client.clientId)}
              deselectedProjects={deselectedProjects}
              onToggleProject={toggleProject}
            />
          ))}
        </div>
      )}
    </Modal>
  )
}

function ApprovalBlock({ clients, onApproved }: { clients: MonthlyPreviewClient[]; onApproved: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const approve = useApproveWeekAction()

  const items = useMemo(() => {
    const map = new Map<string, WeekApproval>()
    for (const client of clients) {
      for (const item of client.missingApprovals.items) {
        map.set(`${item.personId}::${item.isoWeek}`, item)
      }
    }
    return [...map.values()].sort((a, b) => `${a.personName}${a.isoWeek}`.localeCompare(`${b.personName}${b.isoWeek}`))
  }, [clients])

  if (items.length === 0) return null

  const personCount = new Set(items.map((i) => i.personId)).size

  async function approveAll() {
    for (const item of items) {
      await approve.mutateAsync({ weekId: item.isoWeek, personId: item.personId })
    }
    onApproved()
  }

  async function approveOne(item: WeekApproval) {
    await approve.mutateAsync({ weekId: item.isoWeek, personId: item.personId })
    onApproved()
  }

  return (
    <div className="rounded-lg border border-hairline bg-status-approval/10 px-3.5 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1.5 font-medium text-ink"
          aria-expanded={expanded}
        >
          <ChevronDown className={cn('h-4 w-4 text-ink-faint transition-transform', expanded && 'rotate-180')} />
          {items.length} {items.length === 1 ? 'week' : 'weken'} van {personCount} {personCount === 1 ? 'persoon' : 'personen'} nog niet afgesloten
        </button>
        <button type="button" onClick={approveAll} disabled={approve.isPending} className="btn-ghost ml-auto h-8 px-2.5 text-sm">
          Alles afsluiten
        </button>
      </div>
      {expanded ? (
        <ul className="mt-2 divide-y divide-hairline border-t border-hairline">
          {items.map((item) => (
            <li key={`${item.personId}::${item.isoWeek}`} className="flex items-center justify-between gap-3 py-2">
              <span className="text-ink-soft">
                {item.personName} · {weekMetaFromId(item.isoWeek).label}
                {item.state === 'changed' ? ' (gewijzigd)' : ''}
              </span>
              <button type="button" onClick={() => approveOne(item)} disabled={approve.isPending} className="btn-ghost h-7 px-2 text-xs">
                Sluit af
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function PreviewRow({
  client,
  selected,
  onToggle,
  deselectedProjects,
  onToggleProject,
}: {
  client: MonthlyPreviewClient
  selected: boolean
  onToggle: () => void
  deselectedProjects: Set<string>
  onToggleProject: (projectId: string) => void
}) {
  const missingCount = client.missingApprovals.items.length
  const showProjects = selected && client.projectBreakdown.length > 1
  const activeProjects = client.projectBreakdown.filter((p) => !deselectedProjects.has(p.projectId))
  const anyDeselected = client.projectBreakdown.some((p) => deselectedProjects.has(p.projectId))
  const recompute = showProjects && anyDeselected
  const displayHours = recompute ? addDecimals(activeProjects.map((p) => p.hours)) : client.uninvoicedHours
  const displaySubtotal = recompute ? addDecimals(activeProjects.map((p) => p.expectedSubtotal)) : client.expectedSubtotal
  const displayTotal = recompute ? addDecimals(activeProjects.map((p) => p.expectedTotal)) : client.expectedTotal
  return (
    <div
      className={cn(
        'rounded-lg border transition-colors',
        selected ? 'border-accent-500/40 bg-accent-600/5' : 'border-hairline',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3.5 py-3 text-left transition-colors',
          selected ? '' : 'hover:bg-fill-1',
        )}
      >
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
            selected ? 'border-accent-600 bg-accent-600 text-canvas' : 'border-hairline',
          )}
        >
          {selected ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: client.templateColor }} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 font-medium text-ink">
            {client.clientName}
            {client.separateInvoicePerProject && client.projectBreakdown.length > 1 ? (
              <span className="rounded-pill bg-fill-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-faint">
                factuur per project
              </span>
            ) : null}
            {missingCount > 0 ? (
              <Tooltip content={`${missingCount} ${missingCount === 1 ? 'week' : 'weken'} nog niet afgesloten of gewijzigd na akkoord`}>
                <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-status-approval" />
              </Tooltip>
            ) : null}
          </span>
          <span className="block text-sm text-ink-faint">
            {formatHours(toNumber(displayHours))} uur ·{' '}
            {client.personBreakdown.map((p) => p.personName).join(', ')}
          </span>
        </span>
        <Money amount={toNumber(displaySubtotal)} secondaryAmount={toNumber(displayTotal)} className="text-sm font-medium text-ink" />
      </button>
      {client.warnings.length > 0 ? (
        <div className="border-t border-hairline px-3.5 py-2">
          {client.warnings.map((warning) => (
            <p key={warning} className="text-xs text-status-approval">
              {warning}
            </p>
          ))}
        </div>
      ) : null}
      {showProjects ? (
        <div className="border-t border-hairline px-3.5 py-2">
          <p className="label-caps mb-1.5 text-ink-faint">Projecten</p>
          <ul className="space-y-0.5">
            {client.projectBreakdown.map((project) => {
              const projectSelected = !deselectedProjects.has(project.projectId)
              return (
                <li key={project.projectId}>
                  <button
                    type="button"
                    onClick={() => onToggleProject(project.projectId)}
                    className="flex w-full items-center gap-2.5 rounded-md px-1 py-1 text-left text-sm hover:bg-fill-1"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        projectSelected ? 'border-accent-600 bg-accent-600 text-canvas' : 'border-hairline',
                      )}
                    >
                      {projectSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink">{project.projectName}</span>
                    <span className="money shrink-0 text-xs text-ink-faint">
                      {formatHours(toNumber(project.hours))} uur · {formatEuros(toNumber(project.expectedTotal))}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
